import { AxiosError } from 'axios'
import { appConfig } from 'Config/app-config'
import { ExpertsClient } from 'HTTPClients/axios-clients/experts.client'
import { Result, ok, err } from 'neverthrow'
import { ModelFeedback } from 'Types/CustomTask.types'
import {
    AssignmentAtStepType,
    ContainerType,
    CustomGenerateResponsePayload,
    SupplementDataType,
    UpdatedAttributesData,
} from 'Types/Tasks.types'
import { getBearerToken } from 'shared/auth/auth.utils'

export type MetaSubmitGenerateResponseDataPayload = {
    submission: {
        output_container: {
            data: {
                schema_version: number
                type: string
                data: {
                    response: {
                        main_info: {
                            value_type: 'JSON'
                            value: {
                                prompt?: string
                                final_short_answer: string
                                human_correct_response: {
                                    steps: null
                                }
                                prompt_revisions?: {
                                    prompt: string
                                    steps_annotation: string
                                    feedback_from_model?: ModelFeedback[]
                                }[]
                                human_response_needed: boolean
                                prompt_annotation: {
                                    difficulty: string
                                    domain: string
                                    sub_domain: string
                                    time_for_strategy: number
                                    time_to_solve: number
                                }
                                turns: {
                                    retry_count: number
                                    llm_response: string
                                    steps: {
                                        content: string
                                        number: number
                                    }[]
                                }[]
                            }
                        }
                        internal_data: {
                            llm_meta_data: { response: { number_of_responses: number } }[]
                        }
                        is_complete: boolean
                        id: string
                    }
                    quality_check: {
                        evaluation_on_response_version_id: null
                    }
                }
            }
        }
    }
}

export const getAssignmentAtStepApi = async (
    userId: string,
    stepId: string
): Promise<Result<AssignmentAtStepType<ContainerType, unknown>, AxiosError>> => {
    try {
        const payload = {
            user_id: userId,
            step_id: stepId,
        }
        const resp = await ExpertsClient.post<AssignmentAtStepType<ContainerType, unknown>>(
            '/v1/expert-view/task-tool/get-assignment-at-step',
            payload
        )
        return ok(resp.data)
    } catch (e) {
        return err(e as AxiosError)
    }
}

export const postSubmissionTurnData = async (
    userId: string,
    submissionId: string,
    timeElapsedInSeconds: number,
    turnData: unknown,
    attributes: UpdatedAttributesData = {},
    supplement?: SupplementDataType
): Promise<Result<ReadableStreamDefaultReader<Uint8Array>, Error>> => {
    try {
        const payload = {
            user_id: userId,
            is_non_meta: true,
            submission: {
                id: submissionId,
                time_elapsed_in_seconds: timeElapsedInSeconds,
                turn_data: turnData,
                supplement,
                extra_info: attributes,
            },
        }

        const token = getBearerToken()
        const response = await fetch(
            `${appConfig.expertsApiBaseUrl}v1/expert-view/task-tool/submission-play-turn`,
            {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'X-Caller-Service': 'experts-client',
                    ...(token ? { Authorization: token } : {}),
                },
                body: JSON.stringify(payload),
            }
        )

        if (!response.ok || !response.body) {
            throw new Error(`Network error: ${response.status} ${response.statusText}`)
        }

        const reader = response.body.getReader()
        return ok(reader)
    } catch (e) {
        return err(e as Error)
    }
}

export const submitGenerateResponseDataApi = async (
    userId: string,
    submissionId: string,
    timeElapsedInSeconds: number,
    data: CustomGenerateResponsePayload,
    attributes: UpdatedAttributesData = {},
    supplement?: SupplementDataType
): Promise<Result<ReadableStreamDefaultReader<Uint8Array>, Error>> => {
    const turnData = {
        domain: data.domain,
        sub_domain: data.subDomain,
        time_for_strategy: data.timeToDevelopStrategy
            ? parseInt(data.timeToDevelopStrategy as unknown as string)
            : 0,
        time_to_solve: data.timeToSolve ? parseInt(data.timeToSolve as unknown as string) : 0,
        difficulty: data.difficultyLevel?.toUpperCase(),
        complexity: data.complexityLevel,
        accuracy: data.accuracy,
        human_final_answer: data.humanFinalAnswer,
        human_final_answer_reasoning: data.humanFinalAnswerReasoning,
        human_complete_final_answer: data.humanCompleteFinalAnswer,
        model_reasoning_trace: data.modelReasoningTrace,
        model_reasoning_taken_from: data.modelReasoningTakenFrom,
        model_final_answer: data.modelFinalAnswer,
        feedback_level_metrics: data.modelFeedbackDetails?.map(detail => ({
            feedback_usefulness: detail.feedbackUsefulness,
            feedback_result_code: detail.feedbackResultCode,
            feedback_usefulness_justification: detail.feedbackUsefulnessJustification,
        })),
    }
    const finalData = {
        type: data.turnDataType,
        data: turnData,
    }
    return postSubmissionTurnData(
        userId,
        submissionId,
        timeElapsedInSeconds,
        finalData,
        attributes,
        supplement
    )
}

export const submitTurnDataApi = async (
    userId: string,
    submissionId: string,
    timeElapsedInSeconds: number,
    data: unknown
): Promise<Result<ReadableStreamDefaultReader<Uint8Array>, Error>> => {
    return postSubmissionTurnData(userId, submissionId, timeElapsedInSeconds, data)
}
